import { Component, effect, inject, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { MatDrawer, MatDrawerContainer, MatDrawerContent } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatToolbar } from '@angular/material/toolbar';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { JwtService, StorageService } from '@okcargo-apps/services';
import { PwaUpdateService } from '@okcargo-apps/shared-infrastructure';
import { AccountService } from './account/application/account.service';
import { SidebarComponent } from './layout/components/sidebar/sidebar.component';
import { SidenavMobileComponent } from './layout/components/sidenav-mobile/sidenav-mobile.component';
import { TopToolbarComponent } from './layout/components/top-toolbar/top-toolbar.component';
import { ResponsiveService } from './layout/services/responsive.service';
import { ServerEventQueryService } from './shared/infrastructure/server-event-query.service';

@Component({
  imports: [
    RouterModule,
    TranslateModule,
    SidebarComponent,
    MatDrawerContainer,
    MatDrawer,
    MatDrawerContent,
    MatToolbar,
    MatIcon,
    MatIconButton,
    TopToolbarComponent,
    SidenavMobileComponent,
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly accountService = inject(AccountService);
  private readonly translateService = inject(TranslateService);
  private readonly responsiveService = inject(ResponsiveService);
  private readonly storageService = inject(StorageService);
  private readonly jwtService = inject(JwtService);
  private readonly router = inject(Router);
  private readonly serverEventQueryService = inject(ServerEventQueryService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly pwaUpdateService = inject(PwaUpdateService);

  public readonly isMobile = this.responsiveService.isMobile;
  public isAuthenticateUser = signal<boolean>(false);
  // public isVersionSupported$: Observable<boolean | null>;

  @ViewChild('sidenav') sidenav!: MatDrawer;

  constructor() {
    const browserLang = navigator.language || navigator.languages[0];
    const defaultLang = browserLang.startsWith('es') ? 'es' : 'en';
    this.translateService.setDefaultLang(defaultLang);
    this.translateService.use(defaultLang);

    this.accountService.account$.subscribe((account) => {
      if (account && this.translateService.defaultLang !== account.lang) {
        this.translateService.setDefaultLang(account.lang);
        this.translateService.use(account.lang);
      }
    });

    effect(() => {
      if (!this.isMobile() && this.sidenav && this.sidenav.opened) {
        this.sidenav.close();
      }
    });
    // TODO Revise this
    //this.isVersionSupported$ = this.appInfoService.isVersionSupported$();

    effect(() => {
      if (this.pwaUpdateService.isUpdateReady()) {
        this.handlePwaUpdate();
      }
    });
  }

  ngOnInit(): void {
    this.matIconRegistry.setDefaultFontSetClass('material-symbols-outlined'); // Material Symbols as default icons
    const result = {
      accessToken: this.storageService.read('accessToken'),
      refreshToken: this.storageService.read('refreshToken'),
      username: this.storageService.read('username'),
    };
    this.jwtService.userDataReceived$.subscribe((isAuthenticated) => {
      if (isAuthenticated && !this.isAuthenticateUser()) {
        // only create the listener if the user is now authenticated and was not previously authenticated.
        this.serverEventQueryService.listenServerEvents();
      } else if (!isAuthenticated && this.isAuthenticateUser()) {
        this.router.navigate(['/login'], { replaceUrl: true }).then(() => {
          this.redirectToLogin();
        });
      }

      this.isAuthenticateUser.set(isAuthenticated);
    });

    if (result.accessToken) {
      this.jwtService.setAccessToken(result.accessToken);
    }
    if (result.refreshToken) {
      this.jwtService.setRefreshToken(result.refreshToken);
    }
    if (result.username) {
      this.jwtService.setUsername(result.username);
    }
  }

  public redirectToLogin(): void {
    window.location.replace('/login');
  }

  public reloadApp(): void {
    window.location.href = window.location.origin;
  }

  private handlePwaUpdate(): void {
    const snackBarRef = this.snackBar.open('Hay una nueva versión de la aplicación disponible.', 'Actualizar', {
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      duration: 0, // 0 = No se cierra automáticamente
    });

    snackBarRef.onAction().subscribe(() => {
      this.applyUpdate();
    });
  }

  private async applyUpdate(): Promise<void> {
    await this.pwaUpdateService.activateUpdate();
    this.reloadApp();
  }
}
